import type { PointerState, HandleUpParams, SelectionBox } from '@/modules/map-editor';
import type { Node, Edge } from '@/modules/map-editor';
import type { MapAreaService } from '@/modules/map-editor';
import { EDITING_ELEM_TYPES } from '@/models/EDITING_ELEM_TYPES';
import { DASHBOARD_OPTIONS } from '@/models/DASHBOARD_OPTIONS';
import { Scale } from '@/services/canvas/Scale';

/**
 * Service responsible for element selection and deselection orchestration.
 *
 * Handles node selection, edge selection, empty-space clicks, and the
 * two-step edge connection workflow. Delegates cache-rebuild requests to
 * {@link MapAreaService} after toggling selection state.
 */
export class SelectionService {
  /**
   * @param state - Shared reactive pointer state (read/write)
   */
  constructor(private readonly state: PointerState) {}

  // ---------------------------------------------------------------------------
  // Selection handlers
  // ---------------------------------------------------------------------------

  /**
   * Handle click on a node: select it, open QEB, and configure dashboard.
   *
   * @param params - Up interaction parameters containing all callbacks
   */
  async handleNodeSelection(params: HandleUpParams): Promise<void> {
    const {
      nodes,
      currentLayerId,
      mapAreaService,
      saveNodePosition,
      setSelectedNode,
      setSelectedEdge,
      toggleNodeOptions,
      editedNode,
      editedEdge,
      stopEditingNode,
      stopEditingEdge,
      isNodeDrawingMode,
      isNodeDesignSettingsOpened,
      isEdgeDesignSettingsOpened,
      sectionActive,
      chooseOption,
      toggleSection,
      closeSidebar,
      adjustMapForQuickEditBar,
      deselectAllNodes,
      ctx,
      zoomLevel,
      mapArea
    } = params;

    // Send stop editing edge event if editedEdge
    if (editedEdge) await stopEditingEdge();
    // If node edited in QEB and clicked was on another node - send stop editing node event
    if (editedNode && editedNode.id !== this.state.hoveredNodeId) await stopEditingNode();
    // To reopen QEB - set to null first
    await setSelectedNode(null);

    // Clear multi-selection store state
    await deselectAllNodes();
    this.clearSelectedNodes(nodes, currentLayerId, mapAreaService);

    // Store selected node ID
    const nodeSelectedId = this.state.hoveredNodeId;

    // Disable drawing mode if active
    if (isNodeDrawingMode) {
      await chooseOption(null);
    }

    // Check if dashboard section opened different from 'CREATE'
    if (sectionActive !== DASHBOARD_OPTIONS.CREATE.VALUE) {
      await toggleSection(DASHBOARD_OPTIONS.CREATE.VALUE);
    }

    // Show node related group of options
    await toggleNodeOptions(true);

    // Remove edge active ID if previously was opened edge settings
    await setSelectedEdge(null);

    // Set node active ID
    await setSelectedNode(nodeSelectedId);

    // Find selected node and process it
    const selected = nodes.find(node => node.id === nodeSelectedId);
    if (selected) {
      selected.setSelected(true);
      selected.cacheInstanceSetDimensions();
      selected.cache(currentLayerId);
      mapAreaService.updateElement(true, selected.id, EDITING_ELEM_TYPES.NODE);

      // Check if QEB is outside the window area and move map
      await adjustMapForQuickEditBar(selected, ctx, zoomLevel, mapArea);
    }

    // Save position in case of between moving and opening node very little time has passed
    await saveNodePosition('save');

    // Handle design settings opening
    const NODE_DESIGN = DASHBOARD_OPTIONS.CREATE.GROUPS.NODE.NODE_DESIGN;
    if ((isNodeDesignSettingsOpened || isEdgeDesignSettingsOpened) && !selected?.editingAppearance) {
      await chooseOption(NODE_DESIGN);
    }

    // Handle sidebar closing when switching from edge to node settings
    if ((isEdgeDesignSettingsOpened || isNodeDesignSettingsOpened) && selected?.editingAppearance) {
      await closeSidebar();
    }
  }

  /**
   * Handle click on an edge: select it, open dashboard, and configure sidebar.
   *
   * @param params - Up interaction parameters containing all callbacks
   */
  async handleEdgeSelection(params: HandleUpParams): Promise<void> {
    const {
      nodes,
      edges,
      currentLayerId,
      mapAreaService,
      setSelectedNode,
      setSelectedEdge,
      toggleEdgeOptions,
      editedNode,
      editedEdge,
      stopEditingNode,
      stopEditingEdge,
      isNodeDrawingMode,
      isEdgeConnectionsDrawingMode,
      isNodeDesignSettingsOpened,
      isEdgeDesignSettingsOpened,
      sectionActive,
      chooseOption,
      toggleSection,
      closeSidebar,
      cancelEdgeConnection
    } = params;

    const edge = edges.find(e => e.id === this.state.hoveredEdgeId);

    this.clearSelectedNodes(nodes, currentLayerId, mapAreaService);

    // Send stop editing node event if editedNode
    if (editedNode) await stopEditingNode();
    // If edge edited in QEB and click was on another edge - send stop editing edge event
    if (editedEdge && editedEdge.id !== this.state.hoveredEdgeId) await stopEditingEdge();

    // To reopen QEB
    await setSelectedEdge(null);

    // Disable connection drawing mode if active
    if (isEdgeConnectionsDrawingMode) {
      await chooseOption(null);
      await cancelEdgeConnection();
    }

    // Disable node drawing mode if active
    if (isNodeDrawingMode) {
      await chooseOption(null);
    }

    // Store selected edge ID
    const edgeSelectedId = this.state.hoveredEdgeId;

    // Check if dashboard section opened different from 'CREATE'
    if (sectionActive !== DASHBOARD_OPTIONS.CREATE.VALUE) {
      await toggleSection(DASHBOARD_OPTIONS.CREATE.VALUE);
    }

    // Show edge related group of options
    await toggleEdgeOptions(true);

    // Remove node active ID if previously was opened node settings
    await setSelectedNode(null);

    // Set edge active ID
    await setSelectedEdge(edgeSelectedId);

    // Handle design settings opening
    const EDGE_DESIGN = DASHBOARD_OPTIONS.CREATE.GROUPS.EDGE.EDGE_DESIGN;
    if ((isNodeDesignSettingsOpened || isEdgeDesignSettingsOpened) && !edge?.editing) {
      await chooseOption(EDGE_DESIGN);
    }

    // Handle sidebar closing when edge is editing
    if ((isEdgeDesignSettingsOpened || isNodeDesignSettingsOpened) && edge?.editing) {
      await closeSidebar();
    }
  }

  /**
   * Handle click on empty canvas space: deselect everything and close editors.
   *
   * @param params - Up interaction parameters containing all callbacks
   */
  async handleEmptySpaceClick(params: HandleUpParams): Promise<void> {
    const {
      nodes,
      edges,
      currentLayerId,
      mapAreaService,
      selectedNode,
      selectedEdge,
      setSelectedNode,
      setSelectedEdge,
      toggleNodeOptions,
      toggleEdgeOptions,
      isNodeDesignSettingsOpened,
      isEdgeDesignSettingsOpened,
      chooseOption,
      stopEditingNode,
      stopEditingEdge,
      deselectAllNodes,
      selectedCount
    } = params;

    // Clear multi-selection if any
    if (selectedCount > 0) {
      this.clearSelectedNodes(nodes, currentLayerId, mapAreaService);
      await deselectAllNodes();
      // Reset dashboard options when clearing multi-selection
      // (selectedNode is null when size > 1, so the block below won't fire)
      if (!selectedNode) {
        await toggleNodeOptions(false);
      }
    }

    if (selectedNode) {
      await setSelectedNode(null);
      this.clearSelectedNodes(nodes, currentLayerId, mapAreaService);
      await toggleNodeOptions(false);
      if (isNodeDesignSettingsOpened) await chooseOption(null);
      await stopEditingNode();
    }

    if (selectedEdge) {
      await setSelectedEdge(null);
      this.clearSelectedEdges(edges, currentLayerId, mapAreaService);
      await toggleEdgeOptions(false);
      if (isEdgeDesignSettingsOpened) await chooseOption(null);
      await stopEditingEdge();
    }
  }

  // ---------------------------------------------------------------------------
  // Edge connection workflow
  // ---------------------------------------------------------------------------

  /**
   * Handle the two-step edge connection workflow.
   *
   * @param params         - Up interaction parameters
   * @param nodeWasDragged - Whether the pointer dragged a node (blocks connection)
   * @returns `true` if connection mode was active and handled
   */
  async handleEdgeConnection(params: HandleUpParams, nodeWasDragged: boolean): Promise<boolean> {
    const {
      isEdgeConnectionsDrawingMode,
      edgeConnections,
      nodes,
      connectEdge,
      createEdge,
      markNodeConnect
    } = params;

    if (!isEdgeConnectionsDrawingMode || nodeWasDragged || this.state.hoveredNodeId === null || this.state.hoveredNodeId <= -1) {
      return false;
    }

    // Block connection to the same node
    if (edgeConnections.from === this.state.hoveredNodeId) {
      this.state.isMouseDown = false;
      return true;
    }

    // Check if node available for connection
    const hoveredNode = nodes.find(n => n.id === this.state.hoveredNodeId);
    if (edgeConnections.from !== null && edgeConnections.from !== this.state.hoveredNodeId &&
      hoveredNode && !hoveredNode.availableForConnection) {
      this.state.isMouseDown = false;
      return true;
    }

    // Create connection (second step — both from and to are set)
    if (edgeConnections.from !== null && edgeConnections.from !== this.state.hoveredNodeId) {
      this.state.isMouseDown = false;
      await connectEdge(this.state.hoveredNodeId);
      await createEdge();
      return true;
    }

    // First step — mark source node
    if (edgeConnections.from === null) {
      this.state.isMouseDown = false;
      await connectEdge(this.state.hoveredNodeId);
      await markNodeConnect(this.state.hoveredNodeId);
      return true;
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  // Multi-selection handlers
  // ---------------------------------------------------------------------------

  /**
   * Handle Shift+Click on a node: toggle it in/out of multi-selection.
   *
   * @param params - Up interaction parameters containing all callbacks
   */
  async handleMultiNodeSelection(params: HandleUpParams): Promise<void> {
    const {
      nodes,
      currentLayerId,
      mapAreaService,
      selectNode,
      toggleNodeOptions
    } = params;

    const hoveredNode = nodes.find(n => n.id === this.state.hoveredNodeId);
    if (!hoveredNode || hoveredNode.locked || hoveredNode.hiddenLocal) return;

    // Toggle node in the selectedNodes Map
    await selectNode(hoveredNode);

    // Toggle visual selected state on the node (multiSelected=true skips sizer dots)
    hoveredNode.setSelected(!hoveredNode.selected, !hoveredNode.selected);
    hoveredNode.cacheInstanceSetDimensions();
    hoveredNode.cache(currentLayerId);
    mapAreaService.updateElement(true, hoveredNode.id, EDITING_ELEM_TYPES.NODE);

    // Mark all previously selected nodes as multiSelected so they skip sizer dots too
    nodes.forEach(node => {
      if (node.selected && node.id !== hoveredNode.id) {
        node.setSelected(true, true);
        node.cacheInstanceSetDimensions();
        node.cache(currentLayerId);
        mapAreaService.updateElement(true, node.id, EDITING_ELEM_TYPES.NODE);
      }
    });

    // Reset dashboard to default group — node-specific options (NODE_DESIGN,
    // CONTENT_FORM) are only relevant for single selection.
    await toggleNodeOptions(false);

    // No setSelectedNode(null) here — that would clear the nodesSelected Map.
    // getSelectedNode already returns null when selectedNodes.size > 1,
    // so QEB hides automatically for multi-selection.

    mapAreaService.renderFrame();
  }

  /**
   * Handle lasso selection complete: select all nodes within the lasso bounds.
   *
   * @param params       - Up interaction parameters
   * @param selectionBox - The lasso rectangle in canvas coordinates
   * @param ctx          - Canvas context for coordinate transformation
   * @param zoomLevel    - Current zoom level
   */
  async handleLassoSelectionComplete(
    params: HandleUpParams,
    selectionBox: SelectionBox,
    ctx: CanvasRenderingContext2D,
    zoomLevel: number
  ): Promise<void> {
    const { nodes, currentLayerId, mapAreaService, selectMultipleNodes } = params;

    const mapArea = mapAreaService.getState();

    // Convert selection box from screen to world coordinates
    const worldMinX = Math.min(
      Scale.xInv(selectionBox.startX, ctx.canvas.width / 2, zoomLevel) - mapArea.x,
      Scale.xInv(selectionBox.endX, ctx.canvas.width / 2, zoomLevel) - mapArea.x
    );
    const worldMaxX = Math.max(
      Scale.xInv(selectionBox.startX, ctx.canvas.width / 2, zoomLevel) - mapArea.x,
      Scale.xInv(selectionBox.endX, ctx.canvas.width / 2, zoomLevel) - mapArea.x
    );
    const worldMinY = Math.min(
      Scale.yInv(selectionBox.startY, ctx.canvas.height / 2, zoomLevel) - mapArea.y,
      Scale.yInv(selectionBox.endY, ctx.canvas.height / 2, zoomLevel) - mapArea.y
    );
    const worldMaxY = Math.max(
      Scale.yInv(selectionBox.startY, ctx.canvas.height / 2, zoomLevel) - mapArea.y,
      Scale.yInv(selectionBox.endY, ctx.canvas.height / 2, zoomLevel) - mapArea.y
    );

    // Find unlocked nodes whose AABB intersects the lasso rectangle
    const nodesInBounds = nodes.filter(node => {
      if (node.locked || node.hiddenLocal) return false;

      const computedBorder = node.border && node.borderWidth ? node.borderWidth : 0;
      const halfW = (node.width + computedBorder * 2) / 2;
      const halfH = (node.height + computedBorder * 2) / 2;

      const nodeMinX = node.x - halfW;
      const nodeMaxX = node.x + halfW;
      const nodeMinY = node.y - halfH;
      const nodeMaxY = node.y + halfH;

      return nodeMinX < worldMaxX && nodeMaxX > worldMinX &&
             nodeMinY < worldMaxY && nodeMaxY > worldMinY;
    });

    if (nodesInBounds.length > 0) {
      // Set visual selected state (multiSelected=true skips sizer dots)
      nodesInBounds.forEach(node => {
        node.setSelected(true, true);
        node.cacheInstanceSetDimensions();
        node.cache(currentLayerId);
        mapAreaService.updateElement(true, node.id, EDITING_ELEM_TYPES.NODE);
      });

      await selectMultipleNodes(nodesInBounds);

      // Reset dashboard to default group — node-specific options are only for single selection
      await params.toggleNodeOptions(false);
    }

    mapAreaService.renderFrame();
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  /**
   * Clear selected state from all nodes and request cache rebuild.
   *
   * @param nodes          - All canvas nodes
   * @param layerCurrentID - Active layer used for cache invalidation
   * @param mapAreaService - Map area service to schedule element redraws
   */
  clearSelectedNodes(nodes: Node[], currentLayerId: number, mapAreaService: MapAreaService): void {
    nodes.forEach(node => {
      if (node.selected) {
        node.setSelected(false);
        node.cacheInstanceSetDimensions();
        node.cache(currentLayerId);
        mapAreaService.updateElement(true, node.id, EDITING_ELEM_TYPES.NODE);
      }
    });
  }

  /**
   * Clear selected state from all edges and request cache rebuild.
   * Note: Edge.setSelected() has a different signature than Node.setSelected().
   * We directly set `edge.selected = false` instead.
   *
   * @param edges          - All canvas edges
   * @param layerCurrentID - Active layer used for cache invalidation
   * @param mapAreaService - Map area service to schedule element redraws
   */
  clearSelectedEdges(edges: Edge[], currentLayerId: number, mapAreaService: MapAreaService): void {
    edges.forEach(edge => {
      if (edge.selected) {
        edge.selected = false;
        edge.cacheInstanceSetDimensions();
        edge.cache(currentLayerId);
        mapAreaService.updateElement(true, edge.id, EDITING_ELEM_TYPES.EDGE);
      }
    });
  }
}
